import React, { Fragment, useEffect, useState, useMemo } from 'react';
import Navbar from '../../components/Navbar/Navbar';
import PageTitle from '../../components/pagetitle/PageTitle'
import CourseSectionS3 from '../../components/CourseSectionS3/CourseSectionS3';
import Newslatter2 from '../../components/Newslatter2/Newslatter2';
import Scrollbar from '../../components/scrollbar/scrollbar'
import Footer from '../../components/footer/Footer';
import { useParams, Link } from 'react-router-dom';
import IsLoading from '../../components/Loading/IsLoading';
import { useGetAllCoursesMutation, useGetFilteredCoursesMutation } from '../../api/courseApi';
import { Button } from 'reactstrap';
import Search from '../../components/Search/Search';
import MainPageFilter from '../../components/Filters/MainPageFilter';
import { Languages } from '../Extensions/Languages';
import { useGetAllCategoriesQuery } from '../../api/categoryApi';
import { calculateAverageRating } from '../../Helpers/calculateAverageRating';
import { MatchLocationToCurrency } from '../Extensions/MatchLocationToCurrency';
import { useTranslation } from 'react-i18next';
import './coursePage.css';

// Modern Book Card Component
const BookCard = React.memo(({ course }) => {
  const rating = useMemo(() => calculateAverageRating(course.ratings), [course.ratings]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <Link
      onClick={() => window.scrollTo(10, 0)}
      to={`/course-single/${course.courseId}`}
      aria-label={`View course: ${course.courseName}`}
      style={{ textDecoration: 'none' }}
    >
      <div 
        className="book-card"
        onMouseEnter={() => setIsFlipped(true)}
        onMouseLeave={() => setIsFlipped(false)}
      >
        <div className={`book-card-inner ${isFlipped ? 'flipped' : ''}`}>
          <div className="book-spine"></div>
          
          <div className="book-cover">
            {!imageLoaded && <div className="skeleton-loading" style={{height: "100%"}} />}
            <img 
              src={course.courseImage} 
              alt={`${course.courseName} cover`}
              className="book-image"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://via.placeholder.com/400x300?text=Course+Image';
              }}
              loading="lazy"
            />
            
            {course.coursePrice === "0" || course.coursePrice === 0 ? (
              <div className="book-free-badge">ÜCRETSİZ</div>
            ) : (
              <div className="book-price-badge">
                {MatchLocationToCurrency()}{course.coursePrice}
              </div>
            )}
            
            <div className="book-content">
              <h3 className="book-title">{course.courseName}</h3>
              
              <div className="book-rating">
                <div className="book-stars">
                  {[...Array(5)].map((_, i) => (
                    <i 
                      key={i} 
                      className={`fi flaticon-star book-star ${i < Math.floor(rating) ? 'filled' : ''}`}
                    ></i>
                  ))}
                </div>
                <span className="book-rating-text">({rating})</span>
              </div>
              
              <div className="book-stats">
                <span className="book-stat">
                  <i className="fi flaticon-reading-book"></i>
                  {course.student} Öğrenci
                </span>
                <span className="book-stat">
                  <i className="fi flaticon-agenda"></i>
                  {course.lesson} Ders
                </span>
              </div>
            </div>
          </div>
          
          <div className="book-pages">
            <h4 className="book-description-title">Kurs Detayları</h4>
            <p className="book-description">
              {course.courseDescription || 
               "Bu kurs ile alanında uzmanlaşacak, pratik bilgiler edinecek ve kariyer hedefinize ulaşacaksınız."}
            </p>
            
            <ul className="book-details-list">
              <li>
                <i className="fi flaticon-user"></i>
                <strong>Eğitmen:</strong> Uzman Eğitmen
              </li>
              <li>
                <i className="fi flaticon-clock"></i>
                <strong>Süre:</strong> Kendi Hızında
              </li>
              <li>
                <i className="fi flaticon-certificate"></i>
                <strong>Sertifika:</strong> Tamamlama Sertifikası
              </li>
            </ul>
            
            <button className="book-cta-button">
              Kursa Git →
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
});

// Filter Sidebar Component
const FilterSidebar = ({ onFilterChange, initialCategoryId }) => {
  const { t } = useTranslation();
  const { data: categoriesData } = useGetAllCategoriesQuery();
  const [filters, setFilters] = useState({
    searchText: '',
    categoryId: initialCategoryId || '',
    minPrice: '',
    maxPrice: '',
    sortBy: 'name'
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    onFilterChange(filters);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      searchText: '',
      categoryId: initialCategoryId || '',
      minPrice: '',
      maxPrice: '',
      sortBy: 'name'
    };
    setFilters(resetFilters);
    onFilterChange(resetFilters);
  };

  return (
    <div className="filter-sidebar">
      <div className="filter-header">
        <h3>
          <i className="fi flaticon-filter"></i> Filtrele
        </h3>
      </div>
      
      <div className="filter-group">
        <label className="filter-label">Kurs Ara</label>
        <input
          type="text"
          name="searchText"
          value={filters.searchText}
          onChange={handleInputChange}
          placeholder="Kurs adı veya açıklama..."
          className="filter-input"
        />
      </div>

      <div className="filter-group">
        <label className="filter-label">Kategori</label>
        <select
          name="categoryId"
          value={filters.categoryId}
          onChange={handleInputChange}
          className="filter-select"
        >
          <option value="">Tüm Kategoriler</option>
          {categoriesData?.result?.map((category) => (
            <option key={category.categoryId} value={category.categoryId}>
              {category.categoryName}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        <label className="filter-label">Fiyat Aralığı</label>
        <div className="price-range">
          <input
            type="number"
            name="minPrice"
            value={filters.minPrice}
            onChange={handleInputChange}
            placeholder="Min"
            className="filter-input"
          />
          <span className="price-separator">-</span>
          <input
            type="number"
            name="maxPrice"
            value={filters.maxPrice}
            onChange={handleInputChange}
            placeholder="Max"
            className="filter-input"
          />
        </div>
      </div>

      <div className="filter-group">
        <label className="filter-label">Sırala</label>
        <select
          name="sortBy"
          value={filters.sortBy}
          onChange={handleInputChange}
          className="filter-select"
        >
          <option value="name">İsme Göre (A-Z)</option>
          <option value="name_desc">İsme Göre (Z-A)</option>
          <option value="price_asc">Fiyat (Düşük-Yüksek)</option>
          <option value="price_desc">Fiyat (Yüksek-Düşük)</option>
          <option value="popular">Popülerlik</option>
        </select>
      </div>

      <div className="filter-actions">
        <button 
          className="filter-btn apply-btn"
          onClick={handleApplyFilters}
        >
          <i className="fi flaticon-checked"></i>
          Filtrele
        </button>
        <button 
          className="filter-btn reset-btn"
          onClick={handleResetFilters}
        >
          <i className="fi flaticon-refresh"></i>
          Sıfırla
        </button>
      </div>
    </div>
  );
};

// Stil nesnelerini daha organize bir şekilde düzenleyelim
const styles = {
    buttonStyle: {
        padding: '10px 20px',
        width: '150px',
        backgroundColor: '#4CAF50',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        transition: 'background-color 0.3s ease, transform 0.2s ease',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        margin: '10px 0',
    },
    container: {
        padding: '20px 0',
        margin: '0 auto'
    },
    filterContainer: {
        backgroundColor: '#f8f9fa',
        padding: '15px',
        borderRadius: '10px',
        marginBottom: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        margin: '30px 0',
    },
    paginationButton: {
        margin: '0 5px',
    },
    noCoursesMessage: {
        textAlign: 'center',
        padding: '50px 0',
        fontSize: '1.5rem',
        color: '#666'
    }
}


const CoursePage = () => {
    const { t } = useTranslation();
    const {slug} = useParams();
    const [fetchAllDatas] = useGetAllCoursesMutation();
    const [getFilteredCourses] = useGetFilteredCoursesMutation();
    const [courses,setCourses] = useState([])
    const [filteredCourses, setFilteredCourses] = useState([]);
    const [trick,setTrick] = useState(1);
    const [pageCounter,setPageCounter] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filter,setFilter] = useState({
        isSearch:true,
        pageIndex: 1,
        pageSize:9,
        sortColumn:"CourseName",
        sortOrder:"desc",
        filters:{
            groupOp:"AND",
            rules:[
                {
                    field:"CourseEvaluteStatus",
                    op:1,
                    data:"4"
                },
                {
                    field:"CategoryId",
                    op:1,
                    data:slug.toString()
                }
            ]
        }        
    })

    useEffect(() => {
        setLoading(true);
        async function fetchData() {    
            await fetchAllDatas(filter).then((response) => {
                const courseData = response.data.result != [] ? response.data.result.data : [];
                setCourses(courseData);
                setFilteredCourses(courseData);
                setPageCounter(response.data.result.paginationCounter);
                setLoading(false);
            }).catch(error => {
                console.error("Veri yüklenirken bir hata oluştu:", error);
                setLoading(false);
            })
        }
        fetchData();
    },[filter])

    const handleFilterChange = async (filters) => {
        try {
            const result = await getFilteredCourses(filters).unwrap();
            if (result && result.result) {
                setFilteredCourses(result.result);
            }
        } catch (error) {
            console.error('Filter error:', error);
            let filtered = [...courses];
            
            if (filters.searchText) {
                filtered = filtered.filter(course => 
                    course.courseName.toLowerCase().includes(filters.searchText.toLowerCase()) ||
                    (course.courseDescription && course.courseDescription.toLowerCase().includes(filters.searchText.toLowerCase()))
                );
            }
            
            if (filters.categoryId) {
                filtered = filtered.filter(course => course.categoryId === filters.categoryId);
            }
            
            if (filters.minPrice) {
                filtered = filtered.filter(course => parseFloat(course.coursePrice) >= parseFloat(filters.minPrice));
            }
            
            if (filters.maxPrice) {
                filtered = filtered.filter(course => parseFloat(course.coursePrice) <= parseFloat(filters.maxPrice));
            }
            
            switch(filters.sortBy) {
                case 'name':
                    filtered.sort((a, b) => a.courseName.localeCompare(b.courseName));
                    break;
                case 'name_desc':
                    filtered.sort((a, b) => b.courseName.localeCompare(a.courseName));
                    break;
                case 'price_asc':
                    filtered.sort((a, b) => parseFloat(a.coursePrice) - parseFloat(b.coursePrice));
                    break;
                case 'price_desc':
                    filtered.sort((a, b) => parseFloat(b.coursePrice) - parseFloat(a.coursePrice));
                    break;
                case 'popular':
                    filtered.sort((a, b) => (b.student || 0) - (a.student || 0));
                    break;
            }
            
            setFilteredCourses(filtered);
        }
    };

    const handleClickChangePageNumber = (clickedPageNumber) => {
        setFilter((prevFilter) => ({
            ...prevFilter,      
            pageIndex: clickedPageNumber 
        }));
        setTrick(clickedPageNumber);
    };


    if (loading) {
        return <IsLoading />
    }

    return (
        <Fragment>
            <Navbar />
            <PageTitle pageTitle={'Kurslar'} pagesub={'Eğitimlerinizi keşfedin'} />
            
            <section className="modern-course-page">
                <div className="course-page-container">
                    <div className="course-page-layout">
                        {/* Sol Taraf - Filtre Sidebar */}
                        <aside className="filter-sidebar-wrapper">
                            <FilterSidebar 
                                onFilterChange={handleFilterChange}
                                initialCategoryId={slug}
                            />
                        </aside>

                        {/* Sağ Taraf - Kurslar */}
                        <main className="courses-main-content">
                            {filteredCourses.length > 0 ? (
                                <>
                                    <div className="courses-header">
                                        <h2 className="courses-count">
                                            <i className="fi flaticon-book"></i>
                                            {filteredCourses.length} Kurs Bulundu
                                        </h2>
                                    </div>

                                    <div className="book-cards-grid">
                                        {filteredCourses.map((course, key) => (
                                            <BookCard key={key} course={course} />
                                        ))}
                                    </div>
                                    
                                    {/* Modern Pagination */}
                                    <div className="modern-pagination">
                                        {filter.pageIndex !== 1 && (
                                            <button
                                                className="pagination-btn prev-btn"
                                                onClick={() => {
                                                    handleClickChangePageNumber(filter.pageIndex - 1);
                                                    window.scrollTo(0, 0);
                                                }}
                                            >
                                                <i className="fi flaticon-back"></i>
                                                <span>Önceki</span>
                                            </button>
                                        )}
                                        
                                        <div className="pagination-numbers">
                                            {[...Array(pageCounter)].map((_, index) => (
                                                <button
                                                    key={index}
                                                    className={`page-number ${trick === index + 1 ? 'active' : ''}`}
                                                    onClick={() => {
                                                        handleClickChangePageNumber(index + 1);
                                                        window.scrollTo(0, 0);
                                                    }}
                                                >
                                                    {index + 1}
                                                </button>
                                            ))}
                                        </div>
                                        
                                        {filter.pageIndex !== pageCounter && pageCounter > 0 && (
                                            <button
                                                className="pagination-btn next-btn"
                                                onClick={() => {
                                                    handleClickChangePageNumber(filter.pageIndex + 1);
                                                    window.scrollTo(0, 0);
                                                }}
                                            >
                                                <span>Sonraki</span>
                                                <i className="fi flaticon-next"></i>
                                            </button>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div className="no-courses-message">
                                    <div className="empty-state">
                                        <i className="fi flaticon-search"></i>
                                        <h3>Kurs Bulunamadı</h3>
                                        <p>Bu kategoride veya arama kriterlerinize uygun kurs bulunamadı.</p>
                                        <p>Lütfen farklı bir arama veya filtre deneyin.</p>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </section>
            
            <Newslatter2/>
            <Footer />
            <Scrollbar />
        </Fragment>
    )
};
export default CoursePage;
